<template>
    <div>
        <TextField v-model.trim="inputValue"
                   @keyup.enter="handleSearch"
                   :label="$t('labels.search')"
                   :className="{'absolute-details': $vuetify.breakpoint.mdAndUp}"
                   :hideDetails="inputValue.length >= MIN_SEARCH_LENGTH"
                   :hint="$t('hints.search_min_length')"
                   slotName="append">
            <template slot="slotContent">
                <v-icon color="medium_text">{{ ICONS.SEARCH }}</v-icon>
            </template>
        </TextField>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import { mapFields } from 'vuex-map-fields';
import TextField from '@/components/widgets/forms/TextField.vue';
import { ICONS } from '@/constants/icons';

const MIN_SEARCH_LENGTH = 3;

export default {
    name: 'Search',
    components: { TextField },
    props: {
        search: {
            type: String,
            'default': undefined
        }
    },
    data () {
        return {
            ICONS,
            MIN_SEARCH_LENGTH,
            inputValue: '',
            debouncedSearch: null,
            isInternalUpdate: false
        };
    },
    computed: {
        ...mapFields('search', {
            storeSearch: 'search'
        }),
        currentSearch () {
            return this.search !== undefined ? this.search : this.storeSearch;
        }
    },
    watch: {
        inputValue (newValue) {
            const trimmedValue = (newValue || '').trim();
            const searchValue = trimmedValue.length >= MIN_SEARCH_LENGTH ? trimmedValue : '';

            this.isInternalUpdate = true;
            this.updateSearch(searchValue);

            if (searchValue) {
                this.debouncedSearch(searchValue);
            } else if (this.currentSearch) {
                this.$emit('search', '');
            }

            this.$nextTick(() => {
                this.isInternalUpdate = false;
            });
        },
        search: {
            handler (newValue) {
                if (newValue !== undefined && !this.isInternalUpdate) {
                    this.syncInputValue(newValue);
                }
            },
            immediate: true
        },
        storeSearch: {
            handler (newValue) {
                if (this.search === undefined && !this.isInternalUpdate) {
                    this.syncInputValue(newValue);
                }
            },
            immediate: true
        }
    },
    created () {
        this.debouncedSearch = debounce((value) => {
            this.$emit('search', value);
        }, 1000);
    },
    mounted () {
        const initialValue = this.currentSearch || '';
        if (initialValue.length < MIN_SEARCH_LENGTH) {
            this.updateSearch('');
        }
        this.syncInputValue(initialValue);
    },
    methods: {
        updateSearch (value) {
            if (this.search !== undefined) {
                this.$emit('update:search', value);
                this.$emit('input', value);
            } else {
                this.storeSearch = value;
            }
        },
        syncInputValue (value) {
            const trimmedValue = (value || '').trim();
            if (this.inputValue !== trimmedValue) {
                this.inputValue = trimmedValue;
            }
        },
        handleSearch () {
            const trimmedValue = (this.inputValue || '').trim();
            if (trimmedValue.length >= MIN_SEARCH_LENGTH) {
                this.$emit('search', trimmedValue);
            }
        }
    }
};
</script>

<style scoped lang="scss">
    ::v-deep {
        & .v-text-field--enclosed .v-input__append-inner {
            margin-top: 0;
            align-self: center;
        }
        & .v-text-field {
           & input::placeholder{
               font-size: 0.8rem;
           }
        }
        & .absolute-details {
            & > .v-input__control > .v-input__slot {
                margin-bottom: 0;
            }
            & .v-text-field__details {
                margin-top: 5px;
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                width: 100%;
            }
        }
    }
</style>
